import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import {
  Box,
  Typography,
  useTheme,
} from '@mui/material'
import supabase from '../supabaseClient'
import dayjs from 'dayjs'
import { calculateProgress } from '../utils/quranUtils'
import TimePeriodFilter from './ui/TimePeriodFilter'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
)

const ClassStudentQuranProgressChart = ({ classId, teacherId, isAdmin, progressMode = 'verses' }) => {
  const [studentQuranData, setStudentQuranData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('month');
  const theme = useTheme();

  // Handle view mode change
  const handleViewModeChange = useCallback((event, newValue) => {
    if (newValue !== null) {
      setViewMode(newValue);
    }
  }, []);

  // Fetch Quran progress data by student
  useEffect(() => {
    const fetchStudentQuranData = async () => {
      if (!classId) return;

      try {
        setLoading(true);

        // Get current date
        const now = dayjs();
        let startDate;

        // Calculate start date based on view mode
        switch (viewMode) {
          case 'week':
            startDate = now.subtract(7, 'day').format('YYYY-MM-DD');
            break;
          case 'month':
            startDate = now.subtract(30, 'day').format('YYYY-MM-DD');
            break;
          case '6months':
            startDate = now.subtract(180, 'day').format('YYYY-MM-DD');
            break;
          case 'year':
            startDate = now.subtract(365, 'day').format('YYYY-MM-DD');
            break;
          default:
            startDate = now.subtract(30, 'day').format('YYYY-MM-DD');
        }

        let query = supabase
          .from('student_quran_progress')
          .select(
            `
            id,
            date,
            starting_surah,
            starting_verse,
            ending_surah,
            ending_verse,
            assignment_status,
            student_id,
            students(first_name, last_name)
          `,
          )
          .gte('date', startDate)
          .in('assignment_status', ['full', 'partial']);

        if (classId === 'all') {
          if (isAdmin) {
            // For admin, don't filter by class_id to get all classes
          } else if (teacherId) {
            // For teachers, get all their classes
            const { data: classesData, error: classesError } = await supabase
              .from('classes')
              .select('id')
              .eq('teacher_id', teacherId);

            if (classesError) {
              console.error('Error fetching teacher classes:', classesError);
              return;
            }

            if (classesData && classesData.length > 0) {
              const classIds = classesData.map((cls) => cls.id);
              query = query.in('class_id', classIds);
            }
          }
        } else {
          // For a specific class
          query = query.eq('class_id', classId);
        }

        const { data, error } = await query;

        if (error) {
          console.error('Error fetching student Quran progress data:', error);
          return;
        }

        // Group progress data by student
        const studentData = {};
        data.forEach((record) => {
          const studentId = record.student_id;
          const studentName = record.students
            ? `${record.students.first_name} ${record.students.last_name}`
            : `Student ID: ${studentId}`;

          if (!studentData[studentId]) {
            studentData[studentId] = {
              id: studentId,
              name: studentName,
              totalProgress: 0,
              firstStartSurah: record.starting_surah,
              firstStartVerse: record.starting_verse,
              lastEndSurah: record.ending_surah,
              lastEndVerse: record.ending_verse,
              firstDate: record.date,
              lastDate: record.date
            };
          }

          // Calculate total progress (verses or pages) for this record
          const progress = calculateProgress(
            record.starting_surah,
            record.starting_verse,
            record.ending_surah,
            record.ending_verse,
            progressMode
          );

          // Add to total progress for this student
          studentData[studentId].totalProgress += progress;

          // Update range if this is the earliest or latest record
          if (record.date < studentData[studentId].firstDate) {
            studentData[studentId].firstDate = record.date;
            studentData[studentId].firstStartSurah = record.starting_surah;
            studentData[studentId].firstStartVerse = record.starting_verse;
          }
          if (record.date > studentData[studentId].lastDate) {
            studentData[studentId].lastDate = record.date;
            studentData[studentId].lastEndSurah = record.ending_surah;
            studentData[studentId].lastEndVerse = record.ending_verse;
          }
        });

        // Convert to array and sort by total progress completed (descending)
        const studentArray = Object.values(studentData).sort((a, b) => {
          return b.totalProgress - a.totalProgress;
        });

        setStudentQuranData(studentArray);
      } catch (error) {
        console.error('Error in fetchStudentQuranData:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStudentQuranData();
  }, [classId, teacherId, viewMode, isAdmin, progressMode]);

  // Get unit label based on progress mode
  const unitLabel = progressMode === 'pages' ? 'Total Pages Completed' : 'Total Verses Completed'
  const unitName = progressMode === 'pages' ? 'pages' : 'verses'

  // Chart data
  const chartData = useMemo(() => {
    if (studentQuranData.length === 0) return null;

    return {
      labels: studentQuranData.map(student => student.name),
      datasets: [
        {
          label: unitLabel,
          data: studentQuranData.map(student => student.totalProgress),
          backgroundColor: theme.palette.primary.main,
          borderColor: theme.palette.primary.dark,
          borderWidth: 1,
          // Custom data for tooltips
          customData: studentQuranData.map(student => ({
            totalProgress: student.totalProgress,
            firstStartSurah: student.firstStartSurah,
            firstStartVerse: student.firstStartVerse,
            lastEndSurah: student.lastEndSurah,
            lastEndVerse: student.lastEndVerse
          }))
        }
      ],
    };
  }, [studentQuranData, theme, unitLabel]);

  // Chart options
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y', // Horizontal bar chart
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: theme.palette.text.primary,
          font: {
            family: theme.typography.fontFamily,
            size: 12,
          },
        },
      },
      title: {
        display: true,
        text: `Student Quran Progress Overview by ${unitName.charAt(0).toUpperCase() + unitName.slice(1)} (${viewMode.charAt(0).toUpperCase() + viewMode.slice(1)})`,
        color: theme.palette.text.primary,
        font: {
          family: theme.typography.fontFamily,
          size: 16,
          weight: 'bold',
        },
      },
      tooltip: {
        yAlign: 'bottom', // This will position tooltips above the bars
        backgroundColor:
          theme.palette.mode === 'dark'
            ? 'rgba(0, 0, 0, 0.8)'
            : 'rgba(255, 255, 255, 0.8)',
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.primary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            const dataset = context.dataset;
            const dataIndex = context.dataIndex;
            
            if (dataset.customData) {
              const customData = dataset.customData[dataIndex];
              return [
                `${unitLabel}: ${customData.totalProgress}`,
                `Range: ${customData.firstStartSurah} ${customData.firstStartVerse} → ${customData.lastEndSurah} ${customData.lastEndVerse}`
              ];
            }
            
            return `${dataset.label}: ${context.parsed.x}`;
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          color: theme.palette.divider,
        },
        ticks: {
          color: theme.palette.text.secondary,
        },
      },
      y: {
        grid: {
          color: theme.palette.divider,
        },
        ticks: {
          color: theme.palette.text.secondary,
        },
      },
    },
  }), [viewMode, theme, unitLabel, unitName]);

  // Chart container style
  const chartContainerStyle = {
    height: Math.max(400, studentQuranData.length * 30), // Increased height per student
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <TimePeriodFilter value={viewMode} onChange={handleViewModeChange} />
        <Box sx={chartContainerStyle}>
          <Typography variant="body1" color="text.secondary">
            Loading student Quran progress data...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <TimePeriodFilter value={viewMode} onChange={handleViewModeChange} />

      {studentQuranData.length === 0 ? (
        <Box sx={chartContainerStyle}>
          <Typography variant="body1" color="text.secondary">
            No Quran progress data available for the selected period.
          </Typography>
        </Box>
      ) : (
        <>

          <Box sx={chartContainerStyle}>
            <Bar options={chartOptions} data={chartData} />
          </Box>

          {/* Summary statistics */}
          <Box sx={{ 
            mt: 'auto', // This pushes it to the bottom
            p: 2,
            backgroundColor: theme.palette.background.default,
            borderRadius: theme.shape.borderRadius
          }}>
            <Typography variant="subtitle2" gutterBottom>
              About this chart:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Showing {studentQuranData.length} students with recorded progress in the selected period.
            </Typography>
            {studentQuranData.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Students are sorted by total {unitName} completed (highest to lowest). Hover over bars to see progress range.
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default React.memo(ClassStudentQuranProgressChart);